import { action, observable } from 'mobx';
import { api } from '../config';
import { toJS } from 'mobx';

// Helpers
import { getCookie, getPath, toHours, getOverlap } from '../helpers/helpers';

// Dependencies
import moment from 'moment';

// Components
import SessionStore from './SessionStore';
import AdminStore from './AdminStore';

let obx = observable({
  defaultQuery: '?limit=25',
  defaultCourseOffering: true,
  reportFilters: {},
  reportFilterMap: {},
  reportData: null,
  reportTotal: null,
  reportFormat: null,
  reportNext: null,
  headers: {},
  // Export data
  reportMethod: 'GET',
  reportUrl: null,
  reportDefinition: null,
  reportExportTitle: null,
  // Create load more url
  getNextUrl: action(function(url) {
    let nextUrl = getPath(url);
    return nextUrl.replace('/v1', '');
  }),
  // Append data to report
  appendReportData: action(function(data) {
    obx.reportData = obx.reportData.concat(data);
  }),
  // Apply filters to query
  applyFilters: action(function(skipCourseOffering, skipLimit) {
    let query = obx.defaultQuery;

    // If there is a default course offering,
    // add it as a default filter for reports
    if(
      AdminStore.institutionSettings &&
      AdminStore.institutionSettings.defaultCourseOfferingSid &&
      !skipCourseOffering &&
      obx.defaultCourseOffering
    ) {
      obx.reportFilters.courseOfferingSid = AdminStore.institutionSettings.defaultCourseOfferingSid;
    }

    // Create the query string
    if(Object.keys(obx.reportFilters).length > 0) {
      for(let key in obx.reportFilters) {
        if(obx.reportFilters[key]) {
          if(!Array.isArray(toJS(obx.reportFilters[key]))) {
            query += `&${key}=${obx.reportFilters[key]}`;
          } else {
            for(let i = 0; i < obx.reportFilters[key].length; i++) {
              query += `&${key}=${obx.reportFilters[key][i]}`;
            }
          }
        }
      }
    }

    if(skipLimit) {
      query = query.replace('limit=25&', '');
      query = query.replace('limit=25', '');
    }

    return query;
  }),
  // Update a query parameter
  updateQueryParam(url, key, value) {
    const re = new RegExp(`([?&])${key}=.*?(&|$)`, 'i');
    const separator = url.indexOf('?') !== -1 ? '&' : '?';

    if(url.match(re)) {
      return url.replace(re, `$1${key}=${value}$2`);
    } else {
      return `${url}${separator}${key}=${value}`;
    }
  },
  // Generic function to get report data
  getReportData: action(function(report) {
    // Set token header
    if(SessionStore.hasSessionStorage) {
      obx.headers = {'token': sessionStorage.getItem('token')}
    } else {
      obx.headers = {'token': getCookie('token')}
    }

    // Determine which report to get
    switch (report) {
      case 'appointments':
        obx.getAppointmentsReport();
        break;

      case 'approvals':
        obx.getApprovalsReport();
        break;

      case 'automated-proctoring':
        obx.getAutomatedReport();
        break;

      case 'churn':
        obx.getChurnReport();
        break;

      case 'course':
        obx.getCourseReport();
        break;

      case 'exams-by-week':
        obx.getExamsByWeekReport();
        break;

      case 'exam-detail':
        obx.getExamReport('detail');
        break;

      case 'exam-overview':
        obx.getExamReport('overview');
        break;

      case 'exam-volume':
        obx.getExamVolumeReport();
        break;

      case 'incidents':
        obx.getIncidentsReport();
        break;

      case 'instructor-appointments':
        obx.getAppointmentsReport(true);
        break;

      case 'peak-exam':
        obx.getPeakExamReport();
        break;

      case 'session-requests':
        obx.getSessionRequestsReport();
        break;

      case 'unscheduled-learners':
        obx.getUnscheduledLearnersReport();
        break;

      case 'written-audit':
        obx.getWrittenExamsReport();
        break;

      case 'modality':
        obx.getModalityGraphs();
        break;

      default:
        return null;
    }
  }),
  // Report API call
  reportApiCall: action(function(url, passAllData = false) {
    const headers = obx.headers;

    SessionStore.startLoading('Loading…', 'getReportData');

    api.get(url, { headers })
      .then(response => {
        if(!passAllData) {
          obx.reportData = obx.reportFormat(response.data.results);
        } else {
          obx.reportData = obx.reportFormat(response.data);
        }

        obx.reportTotal = response.data.count;

        // Set pagination next url
        if(response.data.next) {
          obx.reportNext = obx.getNextUrl(response.data.next);
        } else {
          obx.reportNext = null;
        }

        SessionStore.finishLoading('getReportData');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  /*-------------------------------------------
    Report: Appointments
  -------------------------------------------*/
  // Format general appointments
  formatAppointmentsReport: action(function(data) {
    const formattedData = [];

    data.forEach(record => {
      // Proctor object
      let proctor = {};

      if(record.schedulingModule === 'instructor-as-proctor') {
        proctor.type = 'Instructor';
      } else if(record.schedulingModule === 'b-virtual') {
        proctor.type = 'B Virtual'
      } else if(record.schedulingModule === 'examity') {
        proctor.type = 'Examity'
      } else if(record.schedulingModule === 'proctoru') {
        proctor.type = 'ProctorU'
      } else if(record.proctor && record.proctor.firstName === null) {
        proctor.type = '';
      } else if(record.proctor) {
        proctor.name = `${record.proctor.firstName} ${record.proctor.lastName}`;
        proctor.email = {
          title: record.proctor.email,
          url: `mailto:${record.proctor.email}`,
          mailto: true
        }
      }

      // Student object
      let student = null;

      if(record.enrollment) {
        student = {
          name: `${record.enrollment.lastName}, ${record.enrollment.firstName}`,
          email: {
            title: record.enrollment.email,
            url: `mailto:${record.enrollment.email}`,
            mailto: true
          }
        };
      }

      // Details link
      let details = null;

      if(record.status.toLowerCase() === 'scheduled' || record.status.toLowerCase() === 'closed') {
        details = {
          url: `?exam=${record.examSid}&session=${record.sid}&course=${record.course.sid}`,
          title: 'View details',
          class: 'btn--outline',
          action: 'SessionDetail',
          actionType: 'drawer',
          skipCSV: true
        }
      }

      const newRow = {
        student: student,
        exam: {
          title: record.exam.title,
          examType: record.exam.type
        },
        course: record.course.title,
        status: record.status,
        appointmentDate: record.appointmentDate,
        proctor: proctor,
        details: details
      }

      formattedData.push(newRow);
    });

    obx.reportDefinition = {
      "Student": "{{enrollment.lastName | customEscape}}, {{enrollment.firstName | customEscape}}",
      "Student Email": "{{enrollment.email | customEscape}}",
      "Exam Title": "{{exam.title | customEscape}}",
      "Exam Type": "{{exam.type | customEscape}}",
      "Course": "{{course.title | customEscape}}",
      "Session ID": "{{sid | customEscape}}",
      "Status": "{{status | customEscape}}",
      "Session Type": "{{sessionType | customEscape}}",
      "Scheduling Module": "{{schedulingModule | customEscape}}",
      "Appointment Date": "{{appointmentDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
      "Proctor": "{% if proctor %}{{proctor.lastName | customEscape}}, {{proctor.firstName | customEscape}}{% endif %}",
      "Proctor Email": "{% if proctor %}{{proctor.email | customEscape}}{% endif %}",
      "Company": "{% if location %}{{location.proctorCompany | customEscape}}{% endif %}",
      "Location Name": "{% if location %}{{location.name | customEscape}}{% endif %}",
      "Address 1": "{% if location %}{{location.address1 | customEscape}}{% endif %}",
      "Address 2": "{% if location %}{{location.address2 | customEscape}}{% endif %}",
      "Region": "{% if location %}{{location.region | customEscape}}{% endif %}",
      "City": "{% if location %}{{location.place | customEscape}}{% endif %}",
      "Postal Code": "{% if location %}{{location.postalCode | customEscape}}{% endif %}",
      "Country": "{% if location %}{{location.country | customEscape}}{% endif %}"
    }

    return formattedData;
  }),
  // Format instructor report
  formatInstructorAppointmentsReport: action(function(data) {
    const formattedData = [];

    data.forEach(record => {
      const newRow = {
        student: `${record.enrollment.lastName}, ${record.enrollment.firstName}`,
        exam: {
          url: `/exams/${record.exam.sid}`,
          title: record.exam.title,
          target: '_self'
        },
        appointmentDate: record.appointmentDate,
        status: record.status,
        details: {
          url: `/instructor-appointments?exam=${record.exam.sid}&session=${record.sid}&course=${record.course.sid}`,
          title: 'View details',
          class: 'btn--outline',
          action: 'SessionManage',
          actionType: 'drawer',
          skipCSV: true
        }
      }

      if(
        record.examConfiguration &&
        record.examConfiguration.instructorAsProctor &&
        record.instructorProctorSid
      ) {
        newRow.location = `<pre>${record.location.address1}</pre>`;
      }

      formattedData.push(newRow);
    });

    return formattedData;
  }),
  // Get appointments report
  getAppointmentsReport: action(function(instructor) {
    // If this is the instructor’s appointment report, only get instructor appointments
    if(instructor) {
      obx.reportFilters.type = 'instructor-as-proctor';
      obx.reportFilters.courseSid = SessionStore.userData.courseSid;
      obx.reportFilters.appointmentAfter = moment().toISOString();
      obx.defaultCourseOffering = false;
      delete obx.reportFilters.courseOfferingSid;
    }

    let query = obx.applyFilters();

    if(!instructor) {
      obx.reportFormat = obx.formatAppointmentsReport;
    } else {
      obx.reportFormat = obx.formatInstructorAppointmentsReport;
    }

    // Set up the filter options
    if(!instructor) {
      obx.reportFilterMap = {
        status: [
          'Needs Proctor',
          'Scheduled',
          'Cancelled',
          'In Progress',
          'Closed'
        ],
        type: [
          'examity',
          'proctoru',
          'b-virtual',
          'register-blast',
          'instructor-as-proctor',
          'messaging',
          'ad-hoc'
        ],
        learnerFirstName: '',
        learnerLastName: '',
        learnerEmail: '',
        learnerSid: '',
        courseTitle: '',
        examTitle: '',
        appointmentAfter: 'date',
        appointmentBefore: 'date'
      };
    } else {
      obx.reportFilterMap = {
        learnerFirstName: '',
        learnerLastName: '',
        examTitle: ''
      };
    }

    // Set the report URL so we can use it to generate a CSV
    let reportQuery = obx.updateQueryParam(query, 'limit', 100);

    obx.reportUrl = `/installs/${SessionStore.userData.installSid}/reports/appointments${reportQuery}`;

    // Make the API call
    obx.reportApiCall(`/installs/${SessionStore.userData.installSid}/reports/appointments${query}`);
  }),
  /*-------------------------------------------
    Report: Proctor approvals
  -------------------------------------------*/
  formatApprovalsReport: action(function(data) {
    const formattedData = data;

    // Store CSV format
    obx.reportDefinition = {
      "Proctor": "{{proctorAccount.accountName | customEscape}}",
      "Status": "{{status | customEscape}}",
      "Installation Scope": "{{appliesTo.applicationInstall.name | customEscape}}",
      "Course Scope": "{{appliesTo.course.title | customEscape}}",
      "Enrollment Scope": "{% if appliesTo.enrollment %}{{appliesTo.enrollment.lastName | customEscape}}, {{appliesTo.enrollment.firstName | customEscape}}{% endif %}",
      "Exam Scope": "{{appliesTo.exam.title | customEscape}}",
      "Exam Session Scope": "{{appliesTo.examSession.sid | customEscape}}",
      "Instructor Scope": "{% if appliesTo.instructorUser %}{{appliesTo.instructorUser.lastName | customEscape}}, {{appliesTo.instructorUser.firstName | customEscape}}{% endif %}",
      "Student Scope": "{% if appliesTo.studentUser %}{{appliesTo.studentUser.lastName | customEscape}}, {{appliesTo.studentUser.firstName | customEscape}}{% endif %}"
    }

    return formattedData;
  }),
  getApprovalsReport: action(function() {
    delete obx.reportFilters.courseOfferingSid;

    let query = obx.applyFilters(true);

    obx.reportFormat = obx.formatApprovalsReport;

    // Set up the filter options
    obx.reportFilterMap = {
      status: [
        'allow',
        'deny',
        'requested'
      ],
      titleFilter: '',
      courseTitle: '',
      examTitle: '',
      requestedByUser: '',
      reviewedByUser: '',
      reviewStartDate: 'date',
      reviewEndDate: 'date'
    };

    // Set the report URL so we can use it to generate a CSV
    let reportQuery = obx.updateQueryParam(query, 'limit', 100);

    obx.reportUrl = `/installs/${SessionStore.userData.installSid}/approvals${reportQuery}`;

    // Make the API call
    obx.reportApiCall(`/installs/${SessionStore.userData.installSid}/approvals${query}`);
  }),
  /*-------------------------------------------
    Report: Automated proctoring
  -------------------------------------------*/
  formatAutomatedReport: action(function(data) {
    const formattedData = [];

    data.forEach(record => {
      let reviewByDate = null;

      if(record.reviewByDate) {
        reviewByDate = moment.utc(record.reviewByDate)
                             .tz(SessionStore.userTimezone)
                             .format('M/D/YYYY [at] h:mma z');
      }

      let reviewCompletedDate = null;

      if(record.reviewCompletedDate) {
        reviewCompletedDate = moment.utc(record.reviewCompletedDate)
                                    .tz(SessionStore.userTimezone)
                                    .format('M/D/YYYY [at] h:mma z');
      }

      const newRow = {
        student: `${record.enrollment.lastName}, ${record.enrollment.firstName}`,
        exam: {
          title: record.exam.title,
          examType: record.exam.type
        },
        course: record.course.title,
        reviewByDate: reviewByDate,
        reviewCompletedDate: reviewCompletedDate,
        reviewRequired: record.reviewRequired ? 'Review Required' : '',
        details: {
          url: `?exam=${record.examSid}&session=${record.sid}&course=${record.course.sid}`,
          title: 'View details',
          class: 'btn--outline',
          action: 'SessionDetail',
          actionType: 'drawer',
          skipCSV: true
        }
      }

      formattedData.push(newRow);
    });

    // Store CSV format
    obx.reportDefinition = {
      "Student": "{{enrollment.lastName | customEscape}}, {{enrollment.firstName | customEscape}}",
      "Exam Title": "{{exam.title | customEscape}}",
      "Exam Type": "{{exam.type | customEscape}}",
      "Course": "{{course.title | customEscape}}",
      "Review By": "{{reviewByDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
      "Reviewed On": "{{reviewCompletedDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
      "Review Required": "{{reviewRequired | customEscape}}"
    }

    return formattedData;
  }),
  getAutomatedReport: action(function() {
    obx.reportFilters.type = 'automated';

    let query = obx.applyFilters();

    obx.reportFormat = obx.formatAutomatedReport;

    // Set up the filter options
    obx.reportFilterMap = {
      'status': [
        'Needs Proctor',
        'Scheduled',
        'Cancelled',
        'In Progress',
        'Closed'
      ],
      'reviewStatus': [
        'pending',
        'complete',
        'not-required'
      ]
    };

    // Set the report URL so we can use it to generate a CSV
    let reportQuery = obx.updateQueryParam(query, 'limit', 100);

    obx.reportUrl = `/installs/${SessionStore.userData.installSid}/reports/appointments${reportQuery}`;

    // Make the API call
    obx.reportApiCall(`/installs/${SessionStore.userData.installSid}/reports/appointments${query}`);
  }),
  /*-------------------------------------------
    Report: Churn
  -------------------------------------------*/
  formatChurnReport: action(function(data) {
    const formattedData = [];

    data.forEach(record => {
      const newRow = {
        student: {
          name: `${record.lastName}, ${record.firstName}`,
          email: {
            title: record.preferredEmail ? record.preferredEmail : record.email,
            url: `mailto:${record.preferredEmail ? record.preferredEmail : record.email}`,
            mailto: true
          }
        },
        exam: {
          examTitle: record.examTitle,
          examType: record.examType,
          courseTitle: record.courseTitle
        },
        currentStatus: record.currentStatus,
        timesCancelled: record.cancelled
      }

      formattedData.push(newRow);
    });

    // Store CSV format
    obx.reportDefinition = {
      "Student": "{{lastName | customEscape}}, {{firstName | customEscape}}",
      "Student Email": "{{email | customEscape}}",
      "Exam Title": "{{examTitle | customEscape}}",
      "Exam Type": "{{examType | customEscape}}",
      "Course Title": "{{courseTitle | customEscape}}",
      "Current Status": "{{currentStatus | customEscape}}",
      "Times Cancelled": "{{cancelled | customEscape}}"
    }

    return formattedData;
  }),
  getChurnReport: action(function() {
    let query = obx.applyFilters();

    obx.reportFormat = obx.formatChurnReport;

    // Set up the filter options
    obx.reportFilterMap = {
      courseTitle: '',
      examTitle: '',
      firstName: '',
      lastName: '',
      email: '',
      status: [
        'Needs Proctor',
        'Scheduled',
        'Cancelled',
        'In Progress',
        'Closed'
      ],
      examOpenDate: 'date',
      examCloseDate: 'date',
      courseOfferingSid: '',
      hasClosed: true,
      hasCancelled: true
    };

    // Set the report URL so we can use it to generate a CSV
    let reportQuery = obx.updateQueryParam(query, 'limit', 100);

    obx.reportUrl = `/installs/${SessionStore.userData.installSid}/reports/churn${reportQuery}`;

    // Make the API call
    obx.reportApiCall(`installs/${SessionStore.userData.installSid}/reports/churn${query}`);
  }),
  /*-------------------------------------------
    Report: Course overview
  -------------------------------------------*/
  formatCourseReport: action(function(data) {
    const formattedData = [];

    // Get token (used for course links)
    let token = null;

    if(SessionStore.hasSessionStorage) {
      token = sessionStorage.getItem('token');
    } else {
      token = getCookie('token');
    }

    // Format the data
    data.forEach(record => {
      const newRow = {
        course: {
          title: {
            title: record.title,
            url: `?token=${token}&courseSid=${record.sid}`
          }
        },
        'Course Id': record.code,
        Instructor: `${record.instructor.firstName} ${record.instructor.lastName}`,
        'Create Date': record.createdDate,
        activeExams: record.activeExams.length,
        learners: parseInt(record.learnerCount, 10),
        details: {
          url: `/reports/course?course=${record.sid}`,
          title: 'View learners',
          class: 'btn--outline',
          action: 'Enrollments',
          actionType: 'drawer',
          skipCSV: true
        }
      }

      formattedData.push(newRow);
    });

    // Store CSV format
    obx.reportDefinition = {
      "Course": "{{title | customEscape}}",
      "Course ID": "{{code | customEscape}}",
      "Instructor": "{{instructor.lastName | customEscape}}, {{instructor.firstName | customEscape}}",
      "Created Date": "{{createdDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
      "Active Exams": "{{activeExams.length | customEscape}}",
      "Students": "{{learnerCount | customEscape}}"
    }

    return formattedData;
  }),
  getCourseReport: action(function() {
    let query = obx.applyFilters();

    obx.reportFormat = obx.formatCourseReport;

    // Set up the filter options
    obx.reportFilterMap = {
      courseOfferingSid: '',
      hasActiveExam: true,
      titleSearchText: '',
      codeSearchText: ''
    };

    // Set the report URL so we can use it to generate a CSV
    let reportQuery = obx.updateQueryParam(query, 'limit', 100);

    obx.reportUrl = `/installs/${SessionStore.userData.installSid}/courses${reportQuery}`;

    // Make the API call
    obx.reportApiCall(`/installs/${SessionStore.userData.installSid}/courses${query}`);
  }),
  /*-------------------------------------------
    Report: Exam detail
  -------------------------------------------*/
  formatExamDetailReport: action(function(data) {
    const formattedData = [];

    // Get token (used for course links)
    let token = null;

    if(SessionStore.hasSessionStorage) {
      token = sessionStorage.getItem('token')
    } else {
      token = getCookie('token')
    }

    // Format the data
    data.forEach(record => {
      let permittedItems = [];

      let lockStatus = {
        button: true,
        title: 'Locked',
        class: 'btn--link report-locked',
        action: 'toggleLock',
        helper: 'Click to unlock',
        sids: {
          examSid: record.sid,
          configurationSid: record.configuration.sid
        }
      };

      if(!record.configuration.locked) {
        lockStatus.title = 'Unlocked';
        lockStatus.class = 'btn--link report-unlocked';
        lockStatus.helper = 'Click to lock';
      }

      if(record.configuration.permittedBathroomBreak) {
        permittedItems.push('Bathroom Break');
      } if(record.configuration.permittedBlankPaper) {
        permittedItems.push('Blank Paper');
      } if(record.configuration.permittedCalculator) {
        permittedItems.push('Calculator');
      } if(record.configuration.permittedDictionary) {
        permittedItems.push('Dictionary');
      } if(record.configuration.permittedEarplugs) {
        permittedItems.push('Earplugs');
      } if(record.configuration.permittedFormulaSheet) {
        permittedItems.push('Formula Sheet');
      } if(record.configuration.permittedNotes) {
        permittedItems.push('Notes');
      } if(record.configuration.permittedTextbook) {
        permittedItems.push('Textbook');
      } if(record.configuration.permittedWebsites) {
        permittedItems.push('Websites');
      } if(record.configuration.permittedOtherItems) {
        permittedItems.push(record.configuration.permittedOtherItems);
      }

      const newRow = {
        course: {
          title: {
            title: record.course.title,
            url: `?token=${token}&courseSid=${record.course.sid}`
          }
        },
        instructor: record.course.instructor.firstName ? `${record.course.instructor.lastName}, ${record.course.instructor.firstName}` : 'Not yet set',
        exam: record.title,
        examType: record.type,
        status: lockStatus,
        duration: {
          raw: record.configuration.duration,
          formatted: toHours(record.configuration.duration, false, true, true)
        },
        password: record.configuration.password,
        attempts: record.configuration.attempts,
        examOpenDate: moment.utc(record.configuration.examOpenDate)
                            .tz(SessionStore.userTimezone)
                            .format('M/D/YYYY [at] h:mma z'),
        examCloseDate: moment.utc(record.configuration.examCloseDate)
                             .tz(SessionStore.userTimezone)
                             .format('M/D/YYYY [at] h:mma z'),
        permittedItems: permittedItems,
        url: record.url,
        studentNotes: record.configuration.studentNotes,
        proctorNotes: record.configuration.proctorNotes,
        scheduleOpenDate:
        record.configuration.scheduleOpenDate === '2000-01-01T05:00:00.000Z' ?
          '' :
          moment.utc(record.configuration.scheduleOpenDate)
            .tz(SessionStore.userTimezone)
            .format('M/D/YYYY [at] h:mma z'),
        scheduleCloseDate:
        record.configuration.scheduleCloseDate === '2070-01-01T05:00:00.000Z' ?
          '' :
          moment.utc(record.configuration.scheduleCloseDate)
            .tz(SessionStore.userTimezone)
            .format('M/D/YYYY [at] h:mma z')
      }

      formattedData.push(newRow);
    });

    // Store CSV format
    obx.reportDefinition = {
      "Course": "{{course.title | customEscape}}",
      "Instructor": "{% if course.instructor.firstName %}{{course.instructor.lastName | customEscape}}, {{course.instructor.firstName | customEscape}}{% else %}Not yet set{% endif %}",
      "Exam": "{{title | customEscape}}",
      "Type": "{{type | customEscape}}",
      "Locked": "{{configuration.locked | customEscape}}",
      "Duration": "{{configuration.duration | customEscape}}",
      "Password": "{{configuration.password | customEscape}}",
      "Opens": "{{configuration.examOpenDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
      "Closes": "{{configuration.examCloseDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
      "Bathroom Break Permitted": "{% if configuration.permittedBathroomBreak %}Yes{% endif %}",
      "Blank Paper Permitted": "{% if configuration.permittedBlankPaper %}Yes{% endif %}",
      "Calculator Permitted": "{% if configuration.permittedCalculator %}Yes{% endif %}",
      "Dictionary Permitted": "{% if configuration.permittedDictionary %}Yes{% endif %}",
      "Earplugs Permitted": "{% if configuration.permittedEarplugs %}Yes{% endif %}",
      "Formula Sheets Permitted": "{% if configuration.permittedFormulaSheet %}Yes{% endif %}",
      "Notes Permitted": "{% if configuration.permittedNotes %}Yes{% endif %}",
      "Textbook Permitted": "{% if configuration.permittedTextbook %}Yes{% endif %}",
      "Websites Permitted": "{% if configuration.permittedWebsites %}Yes{% endif %}",
      "Other Permitted Items": "{{configuration.permittedOtherItems | customEscape}}",
      "URL": "{{url}}",
      "Student Notes": "{{configuration.studentNotes | customEscape}}",
      "Proctor Notes": "{{configuration.proctorNotes | customEscape}}",
      "Scheduling Opens": "{% if configuration.scheduleOpenDate == '2000-01-01T05:00:00.000Z' %}{% else %}{{configuration.scheduleOpenDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}{% endif %}",
      "Scheduling Closes": "{% if configuration.scheduleCloseDate == '2070-01-01T05:00:00.000Z' %}{% else %}{{configuration.scheduleCloseDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}{% endif %}"
    }

    return formattedData;
  }),
  /*-------------------------------------------
    Report: Exam overview
  -------------------------------------------*/
  formatExamOverviewReport: action(function(data) {
    const formattedData = [];

    // Get token (used for course links)
    let token = null;

    if(SessionStore.hasSessionStorage) {
      token = sessionStorage.getItem('token')
    } else {
      token = getCookie('token')
    }

    // Format the data
    data.forEach(record => {
      const instructor = record.course.instructor;

      const newRow = {
        course: {
          title: {
            title: record.course.title,
            url: `?token=${token}&courseSid=${record.course.sid}`
          },
          instructor: instructor.firstName ? `${instructor.firstName} ${instructor.lastName}` : 'Not yet set'
        },
        exam: {
          title: record.title,
          examType: record.type,
          examStatus: (moment().isAfter(record.configuration.scheduleOpenDate) && moment().isBefore(record.configuration.scheduleCloseDate)) ? 'Open' : 'Closed'
        },
        selectionDate: {
          scheduleOpenDate:
          record.configuration.scheduleOpenDate === '2000-01-01T05:00:00.000Z' ?
            '' :
            moment.utc(record.configuration.scheduleOpenDate)
              .tz(SessionStore.userTimezone)
              .format('M/D/YYYY [at] h:mma z'),
          scheduleCloseDate:
          record.configuration.scheduleCloseDate === '2070-01-01T05:00:00.000Z' ?
            '' :
            moment.utc(record.configuration.scheduleCloseDate)
              .tz(SessionStore.userTimezone)
              .format('M/D/YYYY [at] h:mma z')
        },
        examDate: {
          examOpenDate: moment(record.configuration.examOpenDate),
          examCloseDate: moment(record.configuration.examCloseDate)
        },
        incident: record.summary.incident,
        closed: record.summary.closed,
        needsProctor: record.summary.needsProctor,
        scheduled: record.summary.scheduled
      }

      formattedData.push(newRow);
    });

    // Store CSV format
    obx.reportDefinition = {
      "Course": "{{course.title | customEscape}}",
      "Exam": "{{title | customEscape}}",
      "Type": "{{type | customEscape}}",
      "Opens": "{{configuration.examOpenDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
      "Closes": "{{configuration.examCloseDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
      "Scheduling Opens": "{% if configuration.scheduleOpenDate == '2000-01-01T05:00:00.000Z' %}{% else %}{{configuration.scheduleOpenDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}{% endif %}",
      "Scheduling Closes": "{% if configuration.scheduleCloseDate == '2070-01-01T05:00:00.000Z' %}{% else %}{{configuration.scheduleCloseDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}{% endif %}",
      "Incident": "{{summary.incident | customEscape}}",
      "Closed": "{{summary.closed | customEscape}}",
      "Needs Proctor": "{{summary.needsProctor | customEscape}}",
      "Scheduled": "{{summary.scheduled | customEscape}}"
    }

    return formattedData;
  }),
  /*-------------------------------------------
    Report: Exam
    (used for overview and detail reports)
  -------------------------------------------*/
  getExamReport: action(function(report) {
    // Default to sorting by exam title
    if(!obx.reportFilters.sortBy) {
      obx.reportFilters.sortBy = 'examTitle';
    }

    let query = obx.applyFilters();

    if(report === 'overview') {
      obx.reportFormat = obx.formatExamOverviewReport;
    } else {
      obx.reportFormat = obx.formatExamDetailReport;
    }

    // Set up the filter options
    obx.reportFilterMap = {
      courseOfferingSid: '',
      courseTitle: '',
      courseCode: '',
      examTitle: '',
      instructorFirstName: '',
      instructorLastName: '',
      examOpenDate: 'date',
      examCloseDate: 'date',
      sortBy: [
        'examTitle',
        'examOpenDate',
        'examCloseDate',
        'examScheduleOpenDate',
        'examScheduleCloseDate'
      ]
    };

    if(report === 'overview') {
      obx.reportFilterMap.needsProctor = true;
    }

    // Set the report URL so we can use it to generate a CSV
    let reportQuery = obx.updateQueryParam(query, 'limit', 100);

    obx.reportUrl = `/installs/${SessionStore.userData.installSid}/reports/examOverview${reportQuery}`;

    // Make the API call
    obx.reportApiCall(`/installs/${SessionStore.userData.installSid}/reports/examOverview${query}`);
  }),
  /*-------------------------------------------
    Report: Exams by week
  -------------------------------------------*/
  formatExamsByWeekReport: action(function(data) {
    const formattedData = [];
    let weeks = []
    /*
    Alright since we've decided to return exams instead of weeks here's what I'm doing:
     1. Grabbing the start and end of the weeks for the filter and just enumerating through those weeks until I reach the end date  and throwing those weeks into an array
     2. From there the weeks array is now similar to the records in the previous data that was being returned. I have a helper function to check overlap and return the exam if it overlaps with that week at any point.
        moment mutates the original value with some of their functions so that's why I'm cloning start/end of week in the while loop
     */

    const startDate = moment(data.startDate);
    let endDate = moment(data.endDate)
    let enumeratedWeek = startDate.startOf('week');

    while(enumeratedWeek.isAfter(endDate) === false) {
      let startOfWeek = enumeratedWeek.clone()
      let endOfWeek = enumeratedWeek.clone();

      weeks.push({
        weekStart: startOfWeek.startOf('week'),
        weekEnd: endOfWeek.endOf('week')
      })
      enumeratedWeek.add(1, 'weeks');
    }
    weeks.forEach(record => {
      const newRow = {
        start: record.weekStart.format('M/D/YYYY'),
        end: moment(record.weekEnd).format('M/D/YYYY'),
        exams: data.results.filter((exam) => getOverlap(exam.configuration, record))
          .map(exam => {
            return {
              examByWeekCourse: exam.courseName,
              examByWeekExam: exam.title,
              examByWeekType: exam.type,
              examByWeekOpenDate: exam.configuration ? exam.configuration.examOpenDate : '',
              examByWeekCloseDate: exam.configuration ? exam.configuration.examCloseDate : '',
              examByWeekInstructor : exam.instructorName,
              examByWeekDuration: toHours(exam.configuration.duration ,false, true, true),
              examByWeekStatus: exam.status.charAt(0).toUpperCase() + exam.status.slice(1),
              examByWeekStudents: {
                scheduled: `Scheduled: ${exam.totalSessions - exam.needsProctorSessions}`,
                needsProctor: `Needs Proctor: ${exam.needsProctorSessions || 0 }`,
                total: `Total: ${exam.totalSessions || 0 }`
              }
            }
          })
      }
      formattedData.push(newRow);
    });

    // Store CSV format
    obx.reportDefinition = {
      "Active Window": "{{configuration.examOpenDate | date: '%m/%d/%Y'}} to {{configuration.examCloseDate | date: '%m/%d/%Y'}}",
      "Course": "{{courseName | customEscape}}",
      "Exam": "{{title | customEscape}}",
      "Type": "{{type | customEscape}}",
      "Opens": "{{configuration.examOpenDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
      "Closes": "{{configuration.examCloseDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
      "Instructor": "{{instructorName | customEscape}}",
      "Duration": "{{configuration.duration | customEscape}}",
      "Status": "{{status | customEscape}}",
      "Scheduled": "{{totalSessions - needsProctorSessions | customEscape}}",
      "Needs Proctor": "{% if needsProctorSessions != null %}{{needsProctorSessions | customEscape}}{% else %}0{% endif %}"
    };

    return formattedData;
  }),
  getExamsByWeekReport: action(function() {
    obx.reportFormat = obx.formatExamsByWeekReport;

    let query = obx.applyFilters();
    query = query.replace('limit=25', 'limit=50000');

    // Set the report URL so we can use it to generate a CSV
    let reportQuery = obx.updateQueryParam(query, 'limit', 100);

    // Set the report URL so we can use it to generate a CSV
    obx.reportUrl = `/installs/${SessionStore.userData.installSid}/examsByWeek${reportQuery}&startDate=${obx.examsByWeekStart}&endDate=${obx.examsByWeekEnd}`;

    // Make the API call
    obx.reportApiCall(`/installs/${SessionStore.userData.installSid}/examsByWeek${query}&startDate=${obx.examsByWeekStart}&endDate=${obx.examsByWeekEnd}`, true);
  }),
  /*-------------------------------------------
    Report: Exam volume
  -------------------------------------------*/
  formatExamVolumeReport: action(function(data) {
    const formattedData = [];

    // Format the data
    data.forEach(record => {
      const newRow = {
        course: record.courseTitle,
        examDetail: {
          title: record.examTitle,
          examType: record.examType,
          examStatus: (moment().isAfter(record.scheduleOpenDate) && moment().isBefore(record.scheduleCloseDate)) ? 'Open' : 'Closed'
        },
        examDate: {
          examOpenDate: moment(record.examOpenDate),
          examCloseDate: moment(record.examCloseDate)
        },
        selectionDate: {
          scheduleOpenDate:
          record.scheduleOpenDate === '2000-01-01T05:00:00.000Z' ?
            '' :
            moment.utc(record.scheduleOpenDate)
              .tz(SessionStore.userTimezone)
              .format('M/D/YYYY [at] h:mma z'),
          scheduleCloseDate:
          record.scheduleCloseDate === '2070-01-01T05:00:00.000Z' ?
            '' :
            moment.utc(record.scheduleCloseDate)
              .tz(SessionStore.userTimezone)
              .format('M/D/YYYY [at] h:mma z')
        },
        students: record.enrollment,
        unscheduled: record.unscheduledRemaining
      }

      formattedData.push(newRow);
    });

    // Store CSV format
    obx.reportDefinition = {
      "Course": "{{courseTitle | customEscape}}",
      "Exam": "{{examTitle | customEscape}}",
      "Type": "{{examType | customEscape}}",
      "Opens": "{{examOpenDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
      "Closes": "{{examCloseDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
      "Scheduling Opens": "{% if scheduleOpenDate == '2000-01-01T05:00:00.000Z' %}{% else %}{{scheduleOpenDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}{% endif %}",
      "Scheduling Closes": "{% if scheduleCloseDate == '2070-01-01T05:00:00.000Z' %}{% else %}{{scheduleCloseDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}{% endif %}",
      "Students": "{{enrollment | customEscape }}",
      "Unscheduled": "{{unscheduledRemaining | customEscape }}"
    }

    return formattedData;
  }),

  getExamVolumeReport: action(function() {
    obx.reportFormat = obx.formatExamVolumeReport;

    // Set up the filter options
    obx.reportFilterMap = {
      courseOfferingSid: '',
      examOpenDate: 'date',
      examCloseDate: 'date'
    };

    let query = obx.applyFilters();
    query = query.replace('limit=25', 'limit=50000');

    // Set the report URL so we can use it to generate a CSV
    let reportQuery = obx.updateQueryParam(query, 'limit', 100);

    // Set the report URL so we can use it to generate a CSV
    obx.reportUrl = `/installs/${SessionStore.userData.installSid}/examVolume${reportQuery}`;

    // Make the API call
    obx.reportApiCall(`/installs/${SessionStore.userData.installSid}/examVolume${query}`);
  }),

  /*-------------------------------------------
    Report: Incidents
  -------------------------------------------*/
  formatIncidentsReport: action(function(data) {
    const formattedData = [];

    data.forEach(record => {
      const newRow = {
        status: record.status,
        student: {
          name: `${record.lastName}, ${record.firstName}`,
          email: {
            title: record.email,
            url: `mailto:${record.email}`,
            mailto: true
          }
        },
        exam: {
          title: record.examTitle,
          examType: record.examType,
          courseTitle: record.courseTitle
        },
        incidentType: record.title,
        incidentDetails: record.details,
        potentialDamage: record.potentialDamage,
        correctiveActions: record.correctiveActionDetails,
        contact: {
          name: record.contactPerson,
          email: {
            title: record.contactEmail,
            url: `mailto:${record.contactEmail}`,
            mailto: true
          },
          phone: record.contactPhone
        }
      }

      formattedData.push(newRow);
    });

    // Store CSV format
    obx.reportDefinition = {
      "Status": "{{status | customEscape}}",
      "Student": "{{lastName | customEscape}}, {{firstName | customEscape}}",
      "Student Email": "{{email | customEscape}}",
      "Exam Title": "{{examTitle | customEscape}}",
      "Exam Type": "{{examType | customEscape}}",
      "Course Title": "{{courseTitle | customEscape}}",
      "Session Type": "{{sessionType | customEscape}}",
      "Session Status": "{{sessionStatus | customEscape}}",
      "Incident Subject": "{{subject | customEscape}} - {{title | customEscape}}",
      "Incident Details": "{{details | customEscape}}",
      "Potential Damage": "{{potentialDamage | customEscape}}",
      "Witness": "{{directWitness | customEscape}}",
      "Corrective Actions": "{{correctiveActionDetails | customEscape}}",
      "Contact Person": "{{contactPerson | customEscape}}",
      "Contact Email": "{{contactEmail | customEscape}}",
      "Contact Phone": "{{contactPhone | customEscape}}"
    }

    return formattedData;
  }),
  getIncidentsReport: action(function() {
    let query = obx.applyFilters();

    obx.reportFormat = obx.formatIncidentsReport;

    // Set up the filter options
    obx.reportFilterMap = {
      courseTitle: '',
      examTitle: '',
      studentFirstName: '',
      studentLastName: '',
      instructorFirstName: '',
      instructorLastName: '',
      eventTypeSid: [
        'ETf871d52029204b64a12762deece92cc0',
        'ETa628f7235bf54a54b336cfd94b512341',
        'ETcbfeb85c295242de8e6dc2203f254245',
        'ET1a955ec8200845ae8fcd8f3425fa0f57',
        'ET53ab7b51441b4dfc86b017db86d02b86',
        'ET5a46e96cca3a41ee996570c910ea251f',
        'ET03842b6586e6485e805e343909370887',
        'ET7122411fa2544ce690637118b33b7673',
        'ET0342e95534af42afa27d03f86e32c29d'
      ],
      sessionEventStatus: [
        'Open',
        'Closed'
      ]
    };

    // Set the report URL so we can use it to generate a CSV
    let reportQuery = obx.updateQueryParam(query, 'limit', 100);

    obx.reportUrl = `/installs/${SessionStore.userData.installSid}/reports/examSessionEvents${reportQuery}`;

    // Make the API call
    obx.reportApiCall(`installs/${SessionStore.userData.installSid}/reports/examSessionEvents${query}`);
  }),
  /*-------------------------------------------
    Report: Peak Exam
  -------------------------------------------*/
  formatPeakExamReport: action(function(data) {
    const dayOfWeekData = [];
    const hourData = [];
    const dateData = [];

    // Add day of week data
    data.dayOfWeek.forEach(record => {
      const newRow = {
        name: record.day,
        value: record.volume
      };

      dayOfWeekData.push(newRow);
    });

    obx.graphs.push({ data: dayOfWeekData, title: 'by_day_of_week' });

    // Add hour data by looping through
    // all hours of the day
    let dataAdded = false;

    for(let i = 0; i < 24; i ++) {
      let value = 0;

      // Check if any record exists for this hour
      // and if it does, set the value to that
      const record = data.perHour.filter(time => {
        return time.hour === i
      })[0];

      if(record) {
        value = record.volume;
        dataAdded = true;
      }

      console.log(dataAdded);

      // Push the data to the array
      const newRow = {
        name: moment(i, 'H').format('h a'),
        value
      };

      hourData.push(newRow);

      console.log(hourData);

      if(i === 23 && !dataAdded) {
        hourData.length = 0;
      }
    }

    obx.graphs.push({ data: hourData, title: 'by_hour' });

    // Add date data
    data.perDay.forEach(record => {
      const newRow = {
        name: moment(record.day).format('M/D/YYYY'),
        value: record.volume
      };

      dateData.push(newRow);
    });

    obx.graphs.push({ data: dateData, title: 'by_date' });
  }),
  getPeakExamReport: action(function() {
    let query = obx.applyFilters(false, true);

    obx.reportFormat = obx.formatPeakExamReport;

    obx.graphs = [];

    // Set up the filter options
    obx.reportFilterMap = {
      courseOfferingSid: '',
      appointmentDateRangeStart: 'date',
      appointmentDateRangeEnd: 'date',
    };

    // Make the API call
    obx.reportApiCall(
      `installs/${SessionStore.userData.installSid}/reports/appointmentVolume${query}`,
      true
    );
  }),
  /*-------------------------------------------
    Report: Session requests
  -------------------------------------------*/
  formatSessionRequestsReport: action(function(data) {
    const formattedData = [];

    data.forEach(record => {
      let student = null;
      let studentEmail = null;

      if(record.user) {
        student = `${record.user.lastName}, ${record.user.firstName}`;
        studentEmail = record.user.email;
      }

      let proctor = null;
      let proctorEmail = null;

      if(record.proctorAccountUser) {
        proctor = `${record.proctorAccountUser.firstName} ${record.proctorAccountUser.lastName}`;
        proctorEmail = record.proctorAccountUser.email;
      }

      const newRow = {
        student: {
          name: student,
          email: {
            title: studentEmail,
            url: `mailto:${studentEmail}`,
            mailto: true
          }
        },
        proctor: {
          name: proctor,
          email: {
            title: proctorEmail,
            url: `mailto:${proctorEmail}`,
            mailto: true
          }
        },
        requestedDates: [
          moment.utc(record.dateOption1)
            .tz(SessionStore.userTimezone)
            .format('M/D/YYYY [at] h:mma z'),
          moment.utc(record.dateOption2)
            .tz(SessionStore.userTimezone)
            .format('M/D/YYYY [at] h:mma z'),
          moment.utc(record.dateOption3)
            .tz(SessionStore.userTimezone)
            .format('M/D/YYYY [at] h:mma z'),
        ],
        exam: {
          title: record.exam.title,
          examType: record.exam.type
        },
        course: {
          name: record.course.title,
          code: record.course.code
        }
      }

      formattedData.push(newRow);
    });

    // Store CSV format
    obx.reportDefinition = {
      "Student": "{{user.lastName | customEscape}}, {{user.firstName | customEscape}}",
      "Student Email": "{{user.email | customEscape}}",
      "Proctor": "{{proctorAccountUser.lastName | customEscape}}, {{proctorAccountUser.firstName | customEscape}}",
      "Proctor Email": "{{proctorAccountUser.email | customEscape}}",
      "Requested Date 1": "{{dateOption1 | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
      "Requested Date 2": "{{dateOption2 | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
      "Requested Date 3": "{{dateOption3 | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
      "Exam Title": "{{exam.title | customEscape}}",
      "Exam Type": "{{exam.type | customEscape}}",
      "Course": "{{course.title | customEscape}}",
      "Course Code": "{{course.code | customEscape}}"
    }

    return formattedData;
  }),
  getSessionRequestsReport: action(function() {
    obx.defaultCourseOffering = false;
    delete obx.reportFilters.courseOfferingSid;

    let query = obx.applyFilters();

    obx.reportFormat = obx.formatSessionRequestsReport;

    // Set up the filter options
    obx.reportFilterMap = {
      courseTitle: '',
      courseCode: '',
      courseSid: '',
      examTitle: '',
      learnerFirstName: '',
      learnerLastName: '',
      learnerEmail: '',
      proctorFirstName: '',
      proctorLastName: '',
      proctorEmail: ''
    };

    // Set the report URL so we can use it to generate a CSV
    let reportQuery = obx.updateQueryParam(query, 'limit', 100);

    obx.reportUrl = `/installs/${SessionStore.userData.installSid}/reports/sessionRequests${reportQuery}`;

    // Make the API call
    obx.reportApiCall(`installs/${SessionStore.userData.installSid}/reports/sessionRequests${query}`);
  }),
  /*-------------------------------------------
    Report: Unscheduled learners
  -------------------------------------------*/
  formatUnscheduledLearnerReport: action(function(data) {
    const formattedData = [];

    data.forEach(record => {
      let examCloseDate = null;

      if(record.exam.configuration) {
        examCloseDate = moment.utc(record.exam.configuration.examCloseDate)
                              .tz(SessionStore.userTimezone)
                              .format('M/D/YYYY [at] h:mma z');
      }

      const newRow = {
        student: {
          name: `${record.lastName}, ${record.firstName}`,
          email: {
            title: record.email,
            url: `mailto:${record.email}`,
            mailto: true
          }
        },
        exam: {
          title: record.exam.title,
          examType: record.exam.type
        },
        course: {
          name: record.course.title,
          code: record.course.code
        },
        examCloses: examCloseDate
      }

      formattedData.push(newRow);
    });

    // Store CSV format
    obx.reportDefinition = {
      "Student": "{{lastName | customEscape}}, {{firstName | customEscape}}",
      "Student Email": "{{email | customEscape}}",
      "Exam Title": "{{exam.title | customEscape}}",
      "Exam Type": "{{exam.type | customEscape}}",
      "Course": "{{course.title | customEscape}}",
      "Exam Close Date": "{{exam.configuration.examCloseDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}"
    }

    return formattedData;
  }),
  getUnscheduledLearnersReport: action(function() {
    let query = obx.applyFilters();

    obx.reportFormat = obx.formatUnscheduledLearnerReport;

    // Set up the filter options
    obx.reportFilterMap = {
      courseTitle: '',
      courseCode: '',
      examTitle: '',
      learnerFirstName: '',
      learnerLastName: '',
      learnerEmail: ''
    };

    // Set the report URL so we can use it to generate a CSV
    let reportQuery = obx.updateQueryParam(query, 'limit', 100);

    obx.reportUrl = `/installs/${SessionStore.userData.installSid}/reports/unscheduledLearners${reportQuery}`;

    // Make the API call
    obx.reportApiCall(`installs/${SessionStore.userData.installSid}/reports/unscheduledLearners${query}`);
  }),
  /*-------------------------------------------
    Report: Written exams
  -------------------------------------------*/
  formatWrittenExamsReport: action(function(data) {
    const formattedData = [];

    data.forEach(record => {
      const newRow = {
        exam: record.examTitle,
        course: {
          name: record.courseTitle,
          code: record.courseCode
        },
        instructor: {
          name: `${record.instructorFirstName} ${record.instructorLastName}`,
          email: {
            title: record.instructorEmail,
            url: `mailto:${record.instructorEmail}`,
            mailto: true
          }
        },
        configurationType: record.configurationType,
        examOpenDate: moment.utc(record.examOpenDate)
                            .tz(SessionStore.userTimezone)
                            .format('M/D/YYYY [at] h:mma z'),
        examCloseDate: moment.utc(record.examCloseDate)
                             .tz(SessionStore.userTimezone)
                             .format('M/D/YYYY [at] h:mma z')
      }

      formattedData.push(newRow);
    });

    // Store CSV format
    obx.reportDefinition = {
      "Exam": "{{examTitle | customEscape}}",
      "Course Title": "{{courseTitle | customEscape}}",
      "Course Code": "{{courseCode | customEscape}}",
      "Instructor": "{{instructorLastName | customEscape}}, {{instructorFirstName | customEscape}}",
      "Instructor Email": "{{instructorEmail | customEscape}}",
      "Configuration Type": "{{configurationType | customEscape}}",
      "Opens": "{{examOpenDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}",
      "Closes": "{{examCloseDate | time_zone: '"+SessionStore.userTimezone+"', 'M/D/YYYY [at] h:mma z'}}"
    }

    return formattedData;
  }),
  getWrittenExamsReport: action(function() {
    let query = obx.applyFilters();

    obx.reportFormat = obx.formatWrittenExamsReport;

    // Set up the filter options
    obx.reportFilterMap = {
      courseOfferingSid: '',
      examOpenDate: 'date',
      examCloseDate: 'date'
    };

    // Set the report URL so we can use it to generate a CSV
    let reportQuery = obx.updateQueryParam(query, 'limit', 100);

    obx.reportUrl = `/installs/${SessionStore.userData.installSid}/reports/audit${reportQuery}`;

    // Make the API call
    obx.reportApiCall(`installs/${SessionStore.userData.installSid}/reports/audit${query}`);
  }),
  // Generate CSV file
  exportData: action(function(callback) {
    SessionStore.startLoading('Loading…', 'ExportData');

    let urlBase = 'https://api.dev.smarterproctoring.com/v1';

    if(process.env.REACT_APP_ENVIRONMENT === 'stage') {
      urlBase = 'https://api.stage.smarterproctoring.com/v1';
    }

    if(process.env.REACT_APP_ENVIRONMENT === 'production') {
      urlBase = 'https://api.smarterproctoring.com/v1';
    }

    const data = {
      postMetadata: {
        method: obx.reportMethod,
        url: encodeURI(`${urlBase}${obx.reportUrl}`),
        headers: {
          token: SessionStore.token
        }
      },
      exportDocumentProperties: {
        definition: obx.reportDefinition
      },
      title: obx.reportExportTitle
    }

    api.post(`/installs/${SessionStore.userData.installSid}/users/${SessionStore.userData.userSid}/exports`, data)
      .then(response => {
        if(callback) {
          callback();
        }

        SessionStore.finishLoading('ExportData');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  /*-------------------------------------------
    Report: Modality
  -------------------------------------------*/
  graphs: [],
  graphFilters: {},

  getModalityGraphs: action(function(filters, backgroundLoad){
    const { userData } = SessionStore;

    if(!backgroundLoad) {
      SessionStore.startLoading('Loading…', 'getModalityGraphs');
      obx.graphs = [];
    } else {
      SessionStore.startBackgroundLoading('getModalityGraphs');
    }

    let query = '';

    if(userData && userData.role && userData.role.toLowerCase() === 'instructor' && userData.courseSid) {
      query = `?courseSid=${userData.courseSid}`;

      if(filters) {
        Object.keys(filters).forEach(key => {
          query += `&${key}=${filters[key]}`
        });
      }
    } else if(filters) {
      query = '?'

      Object.keys(filters).forEach(key => {
        query += `${key}=${filters[key]}`
      });
    }

    if(filters) {
      obx.graphFilters = filters;
    }

    api.get(`/installs/${SessionStore.userData.installSid}/reports/modalityDistribution${query}`)
      .then(response => {
        const graphData = {
          type: 'institution',
          data: []
        };

        function groupName(group) {
          let name = '';

          switch(group) {
            case 'ad-hoc':
              name = 'Self-managed';
              break;

            case 'automated':
              name = 'Automated';
              break;

            case 'b-virtual':
              name = 'B Virtual';
              break;

            case 'examity':
              name = 'Examity';
              break;

            case 'instructor-as-proctor':
              name = 'Instructor';
              break;

            case 'messaging':
              name = 'Messaging';
              break;

            case 'proctoru':
              name = 'ProctorU';
              break;

            case 'register-blast':
                name = 'RegisterBlast';
                break;

            case 'smarter-scheduling':
              name = 'SmarterScheduling';
              break;

            case 'vtc':
              name = 'VTC';
              break;

            default:
              break;
          }

          return name;
        }

        if(backgroundLoad) {
          obx.graphs = [];
        }

        Object.keys(response.data).forEach(group => {
          graphData.data.push({
            name: groupName(group),
            total: response.data[group].total,
            scheduled: response.data[group].Scheduled,
            closed: response.data[group].Closed
          });
        });

        obx.graphs.push(graphData);

        if(!backgroundLoad) {
          SessionStore.finishLoading('getModalityGraphs');
        } else {
          SessionStore.finishBackgroundLoading('getModalityGraphs');
        }
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Get all generated CSV files
  downloads: [],
  getDownloads: action(function(){
    SessionStore.startLoading('Loading…', 'GetDownloads');

    api.get(`/installs/${SessionStore.userData.installSid}/users/${SessionStore.userData.userSid}/exports`)
      .then(response => {
        obx.downloads = response.data.results;
        SessionStore.finishLoading('GetDownloads');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  })
})

export default obx;



// WEBPACK FOOTER //
// ./src/app/stores/ReportStore.js